import { useState } from 'react';
import type { EnlaceRrpp } from '../../../api/ops/portalRrpp';
import { Boton } from '../../../componentes/Boton';
import { Campo } from '../../../componentes/Campo';
import { Desplegable } from '../../../componentes/Desplegable';
import { PanelLateral } from '../../../componentes/PanelLateral';
import { Ventana } from '../../../componentes/Ventana';
import { useAvisoBreve } from '../../../componentes/avisoBreve';
import { usePermisos } from '../../../permisos/permisos';
import { Bloque, Botonera, Botones, Caja, Dato, Vacia } from '../comun';
import { useAvisarError, useCarga } from '../uso';
import { cuandoEs, cupoEscrito, cupoTexto, nombreRrpp, opsDe, PERMISOS } from '../textos';
import type { ContextoRrpp } from './ApartadoRrpp';
import { EnlaceDeRrpp } from './Enlaces';

/**
 * Un RRPP en el evento (10.10): asignar (`assignPromoterToEvent`: cupo en el evento y en cada lista, en personas, G520;
 * vacío = sin límite, 0 = no puede apuntar; y la lista en la que apunta su enlace) y su enlace del evento (Instagram y
 * formulario web, regenerar y desactivar). «Quitar del evento» (`removePromoterAssignment`) solo sin invitados activos.
 */
export function PanelAsignacion({ ctx, miembro, pestanaInicial }: { ctx: ContextoRrpp; miembro: string | null; pestanaInicial: number }) {
    const { props, evento, rrpp, asignaciones } = ctx;
    const { negocio, movil, soloLectura } = props;
    const ops = opsDe(props.ops);
    const permisos = usePermisos();
    const avisar = useAvisoBreve();
    const avisarError = useAvisarError();
    const a = miembro === null ? undefined : asignaciones.find((x) => x.member_id === miembro && x.status === 'active');
    const nueva = a === undefined;
    const idEvento = evento?.id ?? '';
    const listas = useCarga(() => ops.listGuestLists(negocio, idEvento), [negocio, idEvento]);
    const vivas = (listas.datos?.data ?? []).filter((l) => l.status !== 'cancelled');
    const [pestana, setPestana] = useState(pestanaInicial);
    const [quien, setQuien] = useState(miembro ?? '');
    const [cupo, setCupo] = useState(a?.event_quota == null ? '' : String(a.event_quota));
    const [cupos, setCupos] = useState<Readonly<Record<string, string>>>(() => Object.fromEntries((a?.lists ?? []).map((x) => [x.guest_list_id, x.quota == null ? '' : String(x.quota)])));
    const [apunta, setApunta] = useState(a?.signup_list_id ?? '');
    const [probado, setProbado] = useState(false);
    const [ocupado, setOcupado] = useState(false);
    const [quitar, setQuitar] = useState(false);
    const [enlace, setEnlace] = useState<EnlaceRrpp | null>(a?.event_link ?? null);
    const terminado = evento?.status === 'ended';
    const puede = permisos.puede(PERMISOS.asignar) && !soloLectura && !terminado;
    const p = rrpp.find((x) => x.member_id === (miembro ?? quien));
    const nombre = nombreRrpp(p);
    const libres = rrpp.filter((x) => x.status === 'active' && !asignaciones.some((y) => y.member_id === x.member_id && y.status === 'active'));
    const valor = (id: string) => cupos[id] ?? (nueva ? '' : '0');

    const errores: Partial<Record<string, string>> = {};
    if (probado) {
        if (quien === '') {
            errores.quien = 'Elige el RRPP.';
        }
        const q = cupoEscrito(cupo);
        if (q === undefined) {
            errores.cupo = 'Un número entero o vacío.';
        } else if (a !== undefined && q !== null && q < a.event_used) {
            errores.cupo = `Ya ha apuntado ${String(a.event_used)}: no puede ser menos.`;
        }
        for (const l of vivas) {
            if (cupoEscrito(valor(l.id)) === undefined) {
                errores[l.id] = 'Un número entero o vacío.';
            }
        }
        if (vivas.length > 0 && vivas.every((l) => cupoEscrito(valor(l.id)) === 0)) {
            errores.listas = 'Dale cupo al menos en una lista.';
        }
    }
    const publicas = vivas.filter((l) => l.public_signup === true && cupoEscrito(valor(l.id)) !== 0);

    const guardar = () => {
        const q = cupoEscrito(cupo);
        const listasOk = vivas.every((l) => cupoEscrito(valor(l.id)) !== undefined) && !vivas.every((l) => cupoEscrito(valor(l.id)) === 0);
        if (quien === '' || q === undefined || (a !== undefined && q !== null && q < a.event_used) || !listasOk) {
            setProbado(true);
            return;
        }
        setOcupado(true);
        ops.assignPromoterToEvent(negocio, idEvento, quien, {
            event_quota: q,
            lists: vivas.map((l) => ({ guest_list_id: l.id, quota: cupoEscrito(valor(l.id)) ?? null })),
            signup_list_id: publicas.some((l) => l.id === apunta) ? apunta : (publicas[0]?.id ?? null),
        })
            .then((r) => {
                setEnlace(r.event_link ?? null);
                avisar({ tipo: 'ok', texto: nueva ? `${nombre} ya tiene su enlace para este evento. Lo copia desde su portal; también puedes pasárselo tú.` : 'Cupos guardados.' });
                ctx.recargar();
                if (nueva) {
                    ctx.abrir({ tipo: 'asignar', miembro: quien, pestana: 1 });
                }
            })
            .catch((e: unknown) => { avisarError(e); })
            .finally(() => { setOcupado(false); });
    };

    const confirmarQuitar = () => {
        if (a === undefined) {
            return;
        }
        if (a.event_used > 0) {
            setQuitar(false);
            return;
        }
        setOcupado(true);
        ops.removePromoterAssignment(negocio, idEvento, a.member_id)
            .then(() => {
                avisar({ tipo: 'ok', texto: `${nombre} ya no está en ${evento?.name ?? 'el evento'}.` });
                ctx.recargar();
                ctx.abrir(null);
            })
            .catch((e: unknown) => { avisarError(e); })
            .finally(() => { setOcupado(false); });
    };

    const verCupos = nueva || pestana === 0;
    return (
        <>
            <PanelLateral
                abierto
                onCerrar={() => { ctx.abrir(null); }}
                movil={movil}
                tamano={nueva ? 'formulario' : 'ficha'}
                titulo={nueva ? 'Asignar RRPP' : nombre}
                eyebrow={nueva ? (evento?.name ?? '') : `RRPP · ${evento?.name ?? ''}`}
                subtitulo={nueva ? cuandoEs(evento?.starts_at) : a.event_quota == null ? `${String(a.event_used)} apuntados · sin límite en el evento` : `${String(a.event_used)} de ${String(a.event_quota)} apuntados en el evento`}
                icono={nueva ? 'user-plus' : 'user'}
                accion={verCupos && puede && (!nueva || libres.length > 0) ? (nueva ? 'Asignar' : 'Guardar') : ''}
                accionIcono={nueva ? 'user-plus' : 'check'}
                ocupado={ocupado && !quitar}
                ocupadoTexto={nueva ? 'Asignando…' : 'Guardando…'}
                onAccion={guardar}
            >
                <div className="rv-ca-cuerpo">
                    {!nueva && <Botonera etiqueta="Partes del RRPP en el evento" items={[{ texto: 'Cupos', icono: 'users' }, { texto: 'Enlace', icono: 'instagram' }]} valor={pestana} onCambio={setPestana} />}
                    {verCupos && nueva && (
                        <Bloque>
                            {libres.length === 0 ? (
                                <Vacia icono="users" titulo="Todos tus RRPP activos ya están en este evento" texto="Invita a uno nuevo desde «Tus RRPP»." />
                            ) : (
                                <Desplegable etiqueta="RRPP" valor={quien} onChange={setQuien} error={errores.quien} opciones={[{ valor: '', texto: 'Elige un RRPP' }, ...libres.map((x) => ({ valor: x.member_id, texto: nombreRrpp(x) }))]} />
                            )}
                        </Bloque>
                    )}
                    {verCupos && puede && (nueva ? libres.length > 0 : true) && (
                        <>
                            <Bloque titulo="En el evento">
                                {a !== undefined && <Dato icono="users" k="Apuntados en el evento" v={cupoTexto(a.event_quota, a.event_used)} />}
                                <Campo etiqueta="Cupo en el evento" valor={cupo} onChange={setCupo} modo="numeric" error={errores.cupo} ayuda="Vacío = sin límite · 0 = no puede apuntar." />
                            </Bloque>
                            <Bloque titulo="En cada lista" intro="Vacío = sin límite · 0 = no puede apuntar en esa lista.">
                                {vivas.map((l) => (
                                    <Campo key={l.id} etiqueta={l.name ?? 'Lista'} valor={valor(l.id)} onChange={(v) => { setCupos({ ...cupos, [l.id]: v }); }} modo="numeric" error={errores[l.id]} ayuda={l.status === 'closed' ? 'Lista cerrada.' : undefined} />
                                ))}
                                {vivas.length === 0 && <Caja icono="info">Este evento todavía no tiene listas.</Caja>}
                                {errores.listas !== undefined && <Caja icono="circle-alert" tono="error">{errores.listas}</Caja>}
                            </Bloque>
                            <Bloque titulo="Su enlace de Instagram y su formulario web">
                                {publicas.length > 0 ? (
                                    <Desplegable etiqueta="Apunta en la lista" valor={publicas.some((l) => l.id === apunta) ? apunta : (publicas[0]?.id ?? '')} onChange={setApunta} ayuda="Quien escriba con su enlace queda en esta lista." opciones={publicas.map((l) => ({ valor: l.id, texto: l.name ?? 'Lista' }))} />
                                ) : (
                                    <Caja icono="info">Ninguna de sus listas tiene alta pública: su enlace no apuntará a nadie.</Caja>
                                )}
                                {publicas.find((l) => l.id === apunta)?.web_form_delivery === 'whatsapp' && (
                                    <Caja icono="message-circle" tono="aviso">Esta lista envía el pase del formulario por WhatsApp: cada alta gasta un mensaje del paquete Servicio.</Caja>
                                )}
                            </Bloque>
                            {!nueva && (
                                <Botones nota="Se desactiva su enlace del evento. Solo si no tiene invitados activos.">
                                    <Boton tipo="destructivo" icono="user-minus" onClick={() => { setQuitar(true); }}>Quitar del evento</Boton>
                                </Botones>
                            )}
                        </>
                    )}
                    {verCupos && !puede && a !== undefined && (
                        <Bloque>
                            <Dato icono="users" k="Apuntados en el evento" v={cupoTexto(a.event_quota, a.event_used)} />
                            {a.lists.map((x) => (
                                <Dato key={x.guest_list_id} icono="list-checks" k={vivas.find((l) => l.id === x.guest_list_id)?.name ?? 'Lista'} v={x.quota === 0 ? 'No puede apuntar' : x.quota == null ? 'Sin límite' : `Cupo de ${String(x.quota)}`} />
                            ))}
                            {terminado && <Caja icono="lock">El evento terminó: sus listas y su enlace están cerrados.</Caja>}
                        </Bloque>
                    )}
                    {!nueva && pestana === 1 && (
                        <>
                            {enlace === null ? (
                                <Vacia icono="ban" titulo="Sin enlace" texto="Este RRPP no tiene enlace en este evento." />
                            ) : (
                                <>
                                {enlace.status !== 'active' && <Vacia icono="ban" titulo="Sin enlace activo" texto="Se desactivó. Para darle uno nuevo, regenera el último." />}
                                <EnlaceDeRrpp
                                    props={props}
                                    enlace={enlace}
                                    titulo="Su enlace de este evento"
                                    texto={terminado ? 'El evento terminó: ya no apunta a nadie.' : `${String(enlace.signups ?? 0)} apuntados con él · apuntan en ${vivas.find((l) => l.id === a.signup_list_id)?.name ?? '—'}`}
                                    nombre={nombre}
                                    puede={permisos.puede(PERMISOS.enlacesGestionar) && !soloLectura && !terminado}
                                    onCambio={(n) => { setEnlace(n); ctx.recargar(); }}
                                />
                                </>
                            )}
                            <Caja icono="smartphone">Él lo copia desde su portal de RRPP. Un pase por cuenta de Instagram y evento.</Caja>
                        </>
                    )}
                </div>
            </PanelLateral>
            <Ventana
                abierta={quitar}
                titulo={a !== undefined && a.event_used > 0 ? `No se puede quitar a ${nombre}` : `Quitar a ${nombre} del evento`}
                texto={a !== undefined && a.event_used > 0 ? `Tiene ${String(a.event_used)} invitados activos en este evento. Para quitarlo, primero retíralos o cámbialos de RRPP.` : 'Se desactiva su enlace de este evento para siempre. Puedes volver a asignarlo después.'}
                cancelar={a !== undefined && a.event_used > 0 ? 'Entendido' : 'Cancelar'}
                accion={a !== undefined && a.event_used > 0 ? '' : 'Quitar'}
                accionIcono="user-minus"
                ocupado={ocupado}
                ocupadoTexto="Quitando…"
                movil={movil}
                onAccion={confirmarQuitar}
                onCerrar={() => { if (!ocupado) { setQuitar(false); } }}
            />
        </>
    );
}
